import React, { useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

import Heading from '@components/Heading'
import Section from '@components/Section'
import ScrollIndicator from '@components/ScrollIndicator'
import LayoutHeroMobile from '@components/Layout/Layout.Hero.Mobile'
import Pill from '@components/Pill'
import Transitions from '@components/Transitions'

import media from '@styles/media'

const heroImageUrl = 'https://static.vecteezy.com/system/resources/thumbnails/013/087/516/small/diagonal-golden-line-glass-cube-on-black-background-illustration-of-website-banner-poster-sign-corporate-business-social-media-post-billboard-agency-advertising-media-motion-video-animation-wave-vector.jpg'

function AboutHero() {
  const [animateBulb, setBulbAnimation] = useState(false)

  return (
    <>
      <LayoutHeroMobile>
        <>
          <Section>
            <GridContainer>
              <LeftContainer>
                <Transitions.CSS.FadeIn>
                  <TextContainer>
                    <Pill text="Our Mission" />
                    <Heading.h2 styles="h1">
                      Building Intelligent Systems for Everyday Life
                    </Heading.h2>
                    <MainText>
                      We design and deploy technology that solves real-world inefficiencies across healthcare,
                      commerce, and wellbeing. Our platforms—SwasthaID, Revvlow, and OsadAI—are built to
                      integrate seamlessly into existing systems, enhancing, not replacing.
                    </MainText>
                  </TextContainer>
                </Transitions.CSS.FadeIn>
                <div />
              </LeftContainer>
              <ImageContainer desktop>
                <HeroImageBottom start={animateBulb}>
                  <img
                    src={heroImageUrl}
                    onLoad={() => setBulbAnimation(true)}
                    style={{ width: '100%', display: 'block' }}
                  />
                </HeroImageBottom>
              </ImageContainer>
            </GridContainer>
          </Section>
          <Section>
            <ScrollIndicator />
          </Section>
        </>
      </LayoutHeroMobile>

      <MobileSection hideOnDesktop relative>
        <ImageContainer>
          <HeroImageBottom>
            <img
              src={heroImageUrl}
              onLoad={() => setBulbAnimation(true)}
              style={{ width: '100%', display: 'block' }}
            />
          </HeroImageBottom>
        </ImageContainer>
      </MobileSection>

      <Spacer />
    </>
  )
}

export default AboutHero

const GridContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 505px 1fr;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  height: calc(100vh - 230px);
  min-height: 600px;

  ${media.desktop_medium`
    min-height: 360px;
  `};

  @media screen and (max-height: 800px) {
    min-height: 360px;
  }

  ${media.desktop`
    height: initial
    grid-template-columns: 1fr;
    height: calc(100vh - 140px);
    min-height: 100%;
    padding: 0;
  `};

  ${media.phablet`
    height: calc(100vh - 180px);
    width: 100%;
  `};
`

const TextContainer = styled.div`
  position: relative;

  h1 {
    max-width: 470px;
  }

  ${media.desktop`
    transition-delay: 0ms !important;
    transition-duration: 500ms !important;
  `};

  ${media.phablet`
    top: -50px;
  `}
`

const MainText = styled.p`
  font-size: 3.2rem;
  font-weight: 400;
  color: ${p => p.theme.colors.grey};
  line-height: 1.3;

  ${media.phablet`
    font-size: 2.2rem;
  `};
`

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  max-width: 62.1rem;

  ${media.desktop`
    justify-content: flex-start;
    width: 100%;
    height: initial;
  `};
`

const ImageContainer = styled.div`
  position: absolute;
  width: 100%;
  width: 460px;
  right: -20px;
  justify-self: flex-end;

  ${media.desktop_medium`
    right: 2%;
  `};

  ${media.desktop`
    display:  ${p => (p.desktop ? ' none' : 'block')};
    background: #08070b;
    right: 0;
    left: 0;
    position: relative;
    margin: 0 auto;
    padding-bottom: 60px;
    width: 100%;
  `};
`

const HeroImageTop = styled.div`
  max-width: 161.09px;
  margin: 0 auto;
  position: relative;
  left: -6px;

  ${media.tablet`
    max-width: 130px;
  `}
`

const float = keyframes`
  0%, 100% {
    transform: translatey(0px);
  }

  50% {
    transform: translatey(-8px);
  }
`

const HeroImageBottom = styled.div<{ start: boolean }>`
  max-width: 381.46px;
  margin: 0 auto;
  transform: translateY(${p => (p.start ? '0' : '60px')});
  transition: transform 1.4s var(--ease-in-out-cubic);

  ${p =>
    p.start &&
    css`
      animation: ${float} 4.8s ease-in-out infinite 1.4s;
    `}

  ${media.tablet`
    transform: none;
  `}
`

const Spacer = styled.div`
  background: ${p => p.theme.colors.hero};
  height: 145px;

  ${media.desktop`
    height: 0;
  `};
`

const MobileSection = styled(Section)`
  background: #08070b;
  box-shadow: 0px -40px 40px rgba(8, 7, 11, 0.8);
`
